'use client';

import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { Search, X } from 'lucide-react';
import SearchSuggestionsDropdown from './SearchSuggestionsDropdown';
import SuggestedDestinations from './SuggestedDestinations';
import { useSearchAutocomplete } from '@/hotdesk-ui/hooks/useSearchAutocomplete';
import { useRecentSearches } from '@/hotdesk-ui/hooks/useRecentSearches';
import { RecentSearch } from '@/hotdesk-ui/helpers/recent-searches';
import { useAppDispatch, useAppSelector } from '@/hotdesk-ui/hooks/hook';
import { RootState } from '@/hotdesk-ui/store/store';
import { fetchSuggestedCities, SuggestedCity } from '@/hotdesk-ui/store/slices/suggested-cities.slice';
import { GeoCodeResult } from '@/hotdesk-ui/types/geo-code-result.type';
import { resolveNearbyLocation } from '@/hotdesk-ui/helpers/nearby-location';
import styles from './HeaderSearch.module.scss';
export interface HeaderSearchProps {
  /** Fallback for "Nearby" when geolocation is unavailable or not granted —
   * prompts for the permission. */
  onDetectLocation: () => void;
  /** Search pressed with nothing left to resolve — go to explore as-is. */
  onSearch: () => void;
  /** A place was picked; the caller navigates to explore with it. */
  onSelect: (result: GeoCodeResult) => void;
  /**
   * `header` (default) pins the compact bar into the navbar row.
   * `band` is the landing's larger in-flow pill, which sits in the mobile
   * band under the brand row and reads "Start your search" until typed in.
   */
  variant?: 'header' | 'band';
}

/**
 * The header's own search bar — the compact form of the landing hero search,
 * sized to match the one explore pins into the same slot.
 *
 * It deliberately owns its input ref rather than sharing the navbar's (which
 * belongs to the mobile SearchModal): two components binding one ref would
 * leave the autocomplete listening to whichever mounted last.
 */
const HeaderSearch: FC<HeaderSearchProps> = ({
  onDetectLocation,
  onSearch,
  onSelect,
  variant = 'header'
}) => {
  const isBand = variant === 'band';
  const t = useTranslations();
  const tV4 = useTranslations('landing-v4');
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [hasSearchValue, setHasSearchValue] = useState(false);
  const [nearbyLoading, setNearbyLoading] = useState(false);
  const suggestedCities = useAppSelector((state: RootState) => state.suggestedCities.items);
  const suggestedCitiesLoading = useAppSelector((state: RootState) => state.suggestedCities.loading);
  const geoData = useAppSelector((state: RootState) => state.geoLocation.geoLocationZipData);
  const currentCity = geoData?.city;
  const countryCode = geoData?.country_sort;
  const resolvedAddress = geoData?.formatted_address;
  const {
    recent,
    record,
    clear: clearRecent
  } = useRecentSearches();

  // Every resolved place funnels through here so it is recorded once —
  // autocomplete picks land here via the hook, panel rows call it directly.
  const handleSelect = useCallback((result: GeoCodeResult) => {
    record(result);
    onSelect(result);
  }, [record, onSelect]);
  const {
    containerRef,
    predictions,
    activeIndex,
    isOpen,
    isEmptyFocus,
    setActiveIndex,
    selectByIndex,
    selectActiveOrFirst,
    close,
    resetToEmpty
  } = useSearchAutocomplete({
    inputRef,
    onSelect: handleSelect
  });

  // Warm the suggestions once the country is known, so the empty-focus panel
  // has content the first time the input is focused.
  useEffect(() => {
    if (countryCode) dispatch(fetchSuggestedCities(countryCode));
  }, [countryCode, dispatch]);
  const syncSearchValue = useCallback(() => {
    const value = inputRef.current?.value?.trim() ?? '';
    setHasSearchValue(value.length > 0);
  }, []);

  // Reflect the resolved location in the box — including one picked in the
  // suggestions panel or resolved by "Use my location", which both land in
  // Redux. Setting `value` directly (no input event) keeps the list closed.
  useEffect(() => {
    const input = inputRef.current;
    if (input && resolvedAddress && input.value !== resolvedAddress) {
      input.value = resolvedAddress;
    }
    syncSearchValue();
  }, [resolvedAddress, syncSearchValue]);

  // Every place resolved outside the autocomplete — a panel row, "Nearby" —
  // lands here: show it in the box, then push it down the normal path.
  const commitResult = useCallback((result: GeoCodeResult) => {
    if (inputRef.current) {
      inputRef.current.value = result.formatted_address;
    }
    setHasSearchValue(true);
    close();
    handleSelect(result);
  }, [close, handleSelect]);

  // Panel rows aren't Google predictions; build the same GeoCodeResult shape.
  const selectPlace = useCallback((place: {
    label: string;
    lat: number;
    lng: number;
    city?: string;
    country?: string;
    countryCode?: string;
    placeId?: string;
    /** Country-wide row — must carry the `country` type through. */
    isCountry?: boolean;
    /** State-wide row — its English name, searched as a state. */
    regionName?: string;
  }) => {
    commitResult({
      address_components: [] as unknown as GeoCodeResult['address_components'],
      formatted_address: place.label,
      geometry: {
        location: {
          lat: place.lat,
          lng: place.lng
        },
        location_type: 'APPROXIMATE'
      },
      place_id: place.placeId || '',
      plus_code: {
        compound_code: '',
        global_code: ''
      },
      // Everything downstream (fetch by country name, `countryCode`
      // in the URL, map framing) keys off this type.
      types: (place.isCountry ? ['country'] : place.regionName ? ['administrative_area_level_1'] : []) as unknown as GeoCodeResult['types'],
      country: place.country,
      country_sort: place.countryCode,
      city: place.city,
      ...(place.regionName ? {
        state: place.regionName,
        region_name: place.regionName
      } : {}),
      lat: place.lat,
      log: place.lng,
      source: 'autocomplete'
    } as GeoCodeResult);
  }, [commitResult]);

  // "Nearby" resolves the device's position and then travels the same path as
  // a picked city — so it opens explore on that location. Not granted /
  // refused falls back to the page's permission prompt.
  const handleUseMyLocation = useCallback(() => {
    if (nearbyLoading) return;
    setNearbyLoading(true);
    resolveNearbyLocation(result => {
      setNearbyLoading(false);
      commitResult(result);
    }, () => {
      setNearbyLoading(false);
      close();
      onDetectLocation();
    });
  }, [nearbyLoading, commitResult, close, onDetectLocation]);
  const handleSuggestedCity = useCallback((city: SuggestedCity) => selectPlace({
    label: city.countryName ? `${city.name}, ${city.countryName}` : city.name,
    lat: city.lat,
    lng: city.lng,
    city: city.name,
    country: city.countryName,
    countryCode
  }), [selectPlace, countryCode]);
  const handleRecentSearch = useCallback((entry: RecentSearch) => selectPlace({
    label: entry.label,
    lat: entry.lat,
    lng: entry.lng,
    city: entry.city,
    country: entry.country,
    countryCode: entry.countryCode,
    placeId: entry.id.includes(',') ? undefined : entry.id,
    isCountry: entry.isCountry,
    regionName: entry.regionName
  }), [selectPlace]);

  // If the user typed but didn't pick a suggestion, resolve the
  // highlighted/first one; otherwise search with the location we already have.
  const handleSearchClick = () => {
    if (!selectActiveOrFirst()) onSearch();
  };
  const handleClearSearch = () => {
    if (inputRef.current) inputRef.current.value = '';
    setHasSearchValue(false);
    inputRef.current?.focus();
    // Drop the stale predictions and fall back to suggested destinations.
    resetToEmpty();
  };

  /**
   * The button beside the search icon does double duty. With something
   * typed it clears, as always. With nothing typed there is nothing to
   * clear, so it dismisses the suggestions panel instead — on mobile that
   * panel fills the screen and otherwise offers no visible way out, since
   * tapping away is the only alternative and nothing says so. Typing swaps
   * it straight back to the clear button. Restricted to mobile in CSS: on
   * desktop the panel is a dropdown with the page still visible around it.
   */
  const panelOpen = isOpen || isEmptyFocus;
  const closesPanel = !hasSearchValue && panelOpen;
  const actionVisible = hasSearchValue || closesPanel;
  return <div className={`${styles.headerSearch} ${isBand ? styles.headerSearchBand : ''}`} ref={containerRef} data-sentry-component="HeaderSearch" data-sentry-source-file="HeaderSearch.tsx">
            <div className={styles.searchBar}>
                <div className={styles.searchSegment}>
                    <label className={styles.textContainer}>
                        <span className={styles.segmentLabel}>
                            {tV4('search.whereLabel')}
                        </span>
                        <input ref={inputRef} type="text" className={styles.searchInput} placeholder={tV4(isBand ? 'search.mobileCta' : 'search.wherePlaceholder')} autoCorrect="off" autoCapitalize="off" spellCheck={false} onInput={syncSearchValue} onChange={syncSearchValue} onFocus={syncSearchValue} />
                    </label>
                    <button type="button" className={`${styles.clearBtn} ${actionVisible ? styles.clearBtnVisible : ''} ${closesPanel ? styles.clearBtnClosesPanel : ''}`} onClick={closesPanel ? close : handleClearSearch} aria-label={closesPanel ? t('explore.close') : t('explore.clearSearch')} aria-hidden={!actionVisible} tabIndex={actionVisible ? 0 : -1}>
                        <X size={16} strokeWidth={2.25} data-sentry-element="X" data-sentry-source-file="HeaderSearch.tsx" />
                    </button>
                </div>
                <button type="button" className={styles.searchButton} onClick={handleSearchClick} aria-label="Search">
                    <Search size={18} color="#fff" data-sentry-element="Search" data-sentry-source-file="HeaderSearch.tsx" />
                </button>
            </div>

            {isOpen && <SearchSuggestionsDropdown predictions={predictions} activeIndex={activeIndex} onHover={setActiveIndex} onSelect={selectByIndex} />}

            {/* Nothing typed yet — offer cities in the visitor's country plus
                recent searches, same as the landing and explore. */}
            {!isOpen && isEmptyFocus && <SuggestedDestinations cities={suggestedCities} loading={suggestedCitiesLoading} currentCity={currentCity} onSelect={handleSuggestedCity} onUseMyLocation={handleUseMyLocation} nearbyLoading={nearbyLoading} recent={recent} onSelectRecent={handleRecentSearch} onClearRecent={clearRecent} />}
        </div>;
};
export default HeaderSearch;